import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Col, message, Radio, Row, Space, Table, Tag, Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { StockReceipt, LegalEntity, listReceipts } from '../api/stock';
import StockReceiptInfoDrawer from '../components/StockReceiptInfoDrawer';

const { Title } = Typography;

const StockReceiptsPage: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('orders:write');
  const navigate = useNavigate();

  const [items, setItems] = useState<StockReceipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(30);
  const [legalEntity, setLegalEntity] = useState<LegalEntity | 'all'>('all');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [drawerRefresh, setDrawerRefresh] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listReceipts({
        page, per_page: perPage,
        legal_entity: legalEntity === 'all' ? undefined : legalEntity,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить приходы');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, legalEntity]);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => [
    { title: '№', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Дата', dataIndex: 'date', key: 'date', width: 110 },
    {
      title: 'Юр.лицо', dataIndex: 'legal_entity', key: 'legal_entity', width: 130,
      render: (v: LegalEntity) => (
        <Tag color={v === 'autoregium' ? 'blue' : 'purple'}>
          {v === 'autoregium' ? 'АвтоРегиум' : 'Фомин'}
        </Tag>
      ),
    },
    {
      title: 'Поставщик', dataIndex: 'supplier_name', key: 'supplier_name',
      render: (v: string | null) => v || <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Накладная', dataIndex: 'invoice_number', key: 'invoice_number', width: 140,
      render: (v: string | null) => v || <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Позиций', key: 'lines', width: 90,
      render: (_: unknown, r: StockReceipt) => r.items.length,
    },
  ], []);

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[8, 8]}>
        <Col><Title level={4} style={{ margin: 0 }}>Приходы на склад</Title></Col>
        <Col>
          <Space wrap>
            <Radio.Group value={legalEntity} onChange={(e) => { setPage(1); setLegalEntity(e.target.value); }}>
              <Radio.Button value="all">Все</Radio.Button>
              <Radio.Button value="autoregium">АвтоРегиум</Radio.Button>
              <Radio.Button value="fomin">Фомин</Radio.Button>
            </Radio.Group>
            {canWrite && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/stock/receipt/new')}>
                Новый приход
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        onRow={(r) => ({
          onClick: () => { setDrawerId(r.id); setDrawerOpen(true); },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page, pageSize: perPage, total, showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
      />

      <StockReceiptInfoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        receiptId={drawerId}
        width={560}
        refreshKey={drawerRefresh}
        onDeleted={() => { setDrawerOpen(false); setDrawerRefresh(k => k + 1); load(); }}
      />
    </Card>
  );
};

export default StockReceiptsPage;
